import { defineComponent as _defineComponent } from 'vue'
import {
  ComponentPublicInstance,
  computed,
  ComputedRef,
  defineAsyncComponent,
  onMounted,
  onUnmounted,
  Ref,
  ref,
  watch,
} from 'vue';
import { Location, Route } from 'vue-router';

import PopupMenu from '@/components/dialogs/popupmenu/PopupMenu.vue';
import { PopupMenuItem } from '@/components/dialogs/popupmenu/PopupMenuItem';
import BrandIcon from '@/components/icons/BrandIcon.vue';
import NotificationDot from '@/components/NotificationDot.vue';
import NotificationsPopup from '@/components/NotificationsPopup.vue';
import UserIdent from '@/components/UserIdent.vue';
import { useNotifications } from '@/composables/notification/Notifications';
import { useRoute } from '@/composables/router/VueRouter';
import BrowserDetection from '@/data/BrowserDetection';
import { SearchLocation } from '@/data/datatypes/SemanticSearchQuery';
import { SystemSettingsPage } from '@/data/datatypes/Settings';
import { FullUserDetails } from '@/data/datatypes/UserDetails';
import { UserStatus } from '@/data/datatypes/UserStatus';
import Features from '@/data/Features';
import { formatNotificationCount } from '@/data/helpers/TrackHelper';
import appEventBus from '@/eventbus/EventBus';
import ProfileContextMenu from '@/ProfileContextMenu.vue';
import RouteNames from '@/router/RouteNames';
import { useUIStateStore } from '@/stores/UIState';
import { useUserStore } from '@/stores/User';
import UserStatusIcon from '@/UserStatusIcon.vue';

type OverlayName = typeof overlayNames[number];

/** Type for tracking the open state for each overlay (i.e. `true` if it's open; `false` if it's closed) */
type OverlayOpenStates = {
  [overlay in OverlayName]: { open: boolean, x: number | null, y: number | null };
}

interface Props {
  isHeader?: boolean;
}
interface Emits {
  (e: 'clearAllNotifications'): void,
}

export default /*#__PURE__*/_defineComponent({
  __name: 'NonAppMenu',
  props: {
    isHeader: { type: Boolean, default: true }
  },
  emits: ["clearAllNotifications"],
  setup(__props: any) {

const props = __props as { isHeader: boolean }

const SearchEntryComponent = defineAsyncComponent(() => import('@/components/search/SearchEntryComponent.vue'));
const UserStatusDialog = defineAsyncComponent(() => import('@/UserStatusDialog.vue'));
const DoNotDisturbUntilCustomDialog = defineAsyncComponent(
  () => import('@/components/user/DoNotDisturbUntilCustomDialog.vue'));

const userStore = useUserStore();
const uiStateStore = useUIStateStore();

const route = useRoute();

const { allNotifications } = useNotifications();

/** Names of the available overlay menus */
const overlayNames = ['profileMenu', 'search', 'notification', 'appsMenu'] as const;




const userIdent: Ref<ComponentPublicInstance | null> = ref(null);
const thisMenu: Ref<HTMLDivElement | null> = ref(null);
const notificationIcon: Ref<HTMLDivElement | null> = ref(null);

const searchTerm: Ref<string> = ref('');
const searchOverlayLocation: Ref<SearchLocation> = ref(SearchLocation.ALL);
const headerBottom: Ref<number> = ref(0);
const privacyMode: Ref<boolean> = ref(false);
const currentRoute: Ref<Route> = ref({} as unknown as Route);
const showUserStatusDialog: Ref<boolean> = ref(false);
const displayCustomDoNotDisturbDialog: Ref<boolean> = ref(false);
const overlayOpenStates: Ref<OverlayOpenStates> = ref({
  appsMenu: { open: false, x: -1, y: -1 },
  notification: { open: false, x: -1, y: -1 },
  profileMenu: { open: false, x: -1, y: -1 },
  search: { open: false, x: -1, y: -1 },
});

function isOverlayOpen(overlayName: OverlayName): boolean {
  return (overlayOpenStates.value[overlayName]?.open &&
  (overlayOpenStates.value[overlayName]?.x ?? -1) > -1 &&
  (overlayOpenStates.value[overlayName]?.y ?? -1) > -1);
}

function closeOverlays(...overlayNames: OverlayName[]): void {
  overlayNames.forEach(name => {
    overlayOpenStates.value[name] = { open: false, x: 0, y: 0 };
  });
}

function openOverlays(...overlayNames: OverlayName[]): void {
  overlayNames.forEach(name => {
    openOverlay(name);
  });
}

function openOverlay(overlayName: OverlayName): void {
  let x: number = 0;
  let y: number = 0;

  const headerRect = thisMenu.value?.getBoundingClientRect();
  if (headerRect) {
    headerBottom.value = headerRect.bottom;
    y = headerRect.bottom;

    if (isMobile.value) {
      y = 1;
    } else {
      y -= 8;
    }
  }

  switch (overlayName) {
    case 'profileMenu': {
      const avatarRect = userIdent.value?.$el.getBoundingClientRect();
      if (avatarRect) {
        x = avatarRect.right;
        if (!isMobile.value) {
          // On desktop, move the edge to the centre of the avatar
          x -= avatarRect.width / 2;
        }
      }
      break;
    }
    case 'search': {
      // Search isn't a menu, so doesn't use the PopupMenu component and is handled differently
      break;
    }
    case 'notification': {
      const buttonRect = notificationIcon.value?.getBoundingClientRect();
      if (buttonRect) {
        // Push this to the right a bit to look more connected in the UI
        x = buttonRect.right + (buttonRect.width / 2);
      }
      break;
    }
    case 'appsMenu': {
      break;
    }
  }

  closeAllOverlaysExcept(overlayName);
  overlayOpenStates.value[overlayName].x = x;
  overlayOpenStates.value[overlayName].y = y;
  overlayOpenStates.value[overlayName].open = true;
}

/**
 * Close all overlays, except for the specified one
 * @param overlayName the {@link OverlayName} to keep open
 * @private
 */
function closeAllOverlaysExcept(overlayName: OverlayName): void {
  const toClose: OverlayName[] = [];
  for (const overlay of overlayNames) {
    if (overlay !== overlayName) {
      // OverlayName type is defined from overlayNames; we can cast it
      toClose.push(overlay as OverlayName);
    }
  }

  closeOverlays(...toClose);
}

function closeUserStatusDialog(): void {
  showUserStatusDialog.value = false;
}

function userStatusClicked(): void {
  showUserStatusDialog.value = true;
}

const currentUser: ComputedRef<FullUserDetails> = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return userStore.currentUser!;
});

const currentUserStatus: ComputedRef<UserStatus | null> = computed(() => {
  return userStore.currentUserStatus;
});

const userDisplayName: ComputedRef<string | null> = computed(() => {
  return userStore.userDisplayName;
});

const isHomeRoute: ComputedRef<boolean> = computed(() => {
  return route.name === RouteNames.HOME_ROUTE_NAME;
});

const isMobile: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isMobile();
});

const isMobileHeader: ComputedRef<boolean> = computed(() => {
  return props.isHeader && isMobile.value;
});

const numNotifications: ComputedRef<number> = computed(() => {
  return allNotifications.value.length;
});

const formattedNotificationCount: ComputedRef<string | null> = computed(() => {
  return formatNotificationCount(numNotifications.value);
});

const selectedPage: ComputedRef<boolean> = computed(() => {
  return route.name === RouteNames.NOTIFICATIONS_ROUTE_NAME;
});

const showHelpIcon: ComputedRef<boolean> = computed(() => {
  return !!((window as unknown) as { pendo: unknown }).pendo && !isMobile.value;
});

function openSearchOverlay(location: SearchLocation = currentLocation.value): void {
  searchOverlayLocation.value = location;
  openOverlays('search');
}

const currentLocation: ComputedRef<SearchLocation> = computed(() => {
  return uiStateStore.searchMenuOverlayLocation;
});

const extraMenuItems: ComputedRef<PopupMenuItem[]> = computed(() => {
  const items: PopupMenuItem[] = [];
  if (shouldShowKBItem.value) {
    addOverflowMenuItem(items, 'Knowledge management', 'flat/book-compact',
      RouteNames.KNOWLEDGE_MANAGEMENT_ROUTE_NAME);
  }
  if (shouldShowWorkspacePublishingItem.value) {
    // TODO: correct icon
    addOverflowMenuItem(items, 'Publisher', 'flat/upload-compact',
      RouteNames.PUBLISH_TEMPLATES_ROUTE_NAME);
  }
  if (userStore.isReportingUser) {
    addOverflowMenuItem(items, 'Reporting', 'flat/stats-compact',
      RouteNames.REPORTING_ROUTE_NAME);
  }
  if (userStore.isUserTenantAdmin) {
    addOverflowMenuItem(items, 'Tenant settings', 'flat/settings-compact',
      RouteNames.TENANT_SETTINGS_ROUTE_NAME, { tenantId: 'me', page: 'details' });
  }
  if (userStore.isUserSystemAdmin) {
    // TODO: correct icon
    addOverflowMenuItem(items, 'System settings', 'flat/sliders-vertical-compact',
      RouteNames.SYSTEM_SETTINGS_ROUTE_NAME, { page: SystemSettingsPage.SYSTEM });
  }

  return items;
});

const shouldShowWorkspacePublishingItem: ComputedRef<boolean> = computed(() => {
  return userStore.isUserFeatureEnabled(Features.PUBLISH_ROOMS) && userStore.isUserTenantAdmin;
});

// visible if feature toggle on, and wiki exists
const shouldShowKBItem: ComputedRef<boolean> = computed(() => {
  return userStore.isUserFeatureEnabled(Features.KNOWLEDGE_BASE) &&
    !!userStore.currentTokenIfValid &&
    !!userStore.currentTokenIfValid.kbWikiId &&
    userStore.currentTokenIfValid.kbWikiId.length > 0;
});

const selectedOverflowMenuItem: ComputedRef<PopupMenuItem | undefined> = computed(() => {
  if (!currentRoute.value || !extraMenuItems.value?.length) {
    return;
  }
  return extraMenuItems.value.find((item: PopupMenuItem) => {
    if (!item.route) {
      return false;
    }
    let routeName: string | undefined;
    // RouteLocationRaw is 'string | Location'
    if (typeof item.route === 'string') {
      routeName = item.route as string;
    } else {
      routeName = (item.route as Location).name;
    }
    if (!routeName) {
      return false;
    }
    return routeNameMatchesCurrentRoute(routeName);
  });
});

function routeNameMatchesCurrentRoute(routeName: string): boolean {
  // This is a bit hacky, it's because the reporting routes are all defined at the same level.
  if (routeName === RouteNames.REPORTING_ROUTE_NAME) {
    const currentRouteName: string | null | undefined = currentRoute.value.name;
    return currentRouteName === routeName ||
      currentRouteName === RouteNames.REPORTING_TENANT_ROUTE_NAME;
  }
  return currentRoute.value.matched.some((routeRecord) => {
    return routeRecord.name === routeName;
  });
}

const overflowMenuItemSelected: ComputedRef<boolean> = computed(() => {
  return !!selectedOverflowMenuItem.value;
});

function addOverflowMenuItem(items: PopupMenuItem[], name: string, icon: string, routeName: string,
  routeParams?: Record<string, string>): void {
  let cssClass: string | undefined;
  if (routeNameMatchesCurrentRoute(routeName)) {
    icon = icon.replace('-light', '').replace('-compact', '') + '-solid';
    cssClass = 'selected';
  }
  items.push(new PopupMenuItem('item',
    name,
    {
      optionsType: 'router',
      route: { name: routeName, params: routeParams },
      icon,
      cssClass,
    }
  ));
}

function closeSearchOverlay(): void {
  searchOverlayLocation.value = SearchLocation.ALL;
  closeOverlays('search');
}

function togglePrivacyMode(): void {
  privacyMode.value = !privacyMode.value;
}

function toggleAppearOfflineClicked(): void {
  currentUser.value.appearOffline = !currentUser.value.appearOffline;
  userStore.updateCurrentUser(currentUser.value);
}

function showCustomDoNotDisturbDialog(): void {
  displayCustomDoNotDisturbDialog.value = true;
}

function hideCustomDoNotDisturbDialog(): void {
  displayCustomDoNotDisturbDialog.value = false;
}

function displayNotificationCount(num: number): string {
  if (num === 1) {
    return '1 notification';
  }
  return `${num} notifications`;
}

watch(() => privacyMode.value, (newValue: boolean) => {
  uiStateStore.setPrivacyMode(newValue);
});

watch(() => route, (newValue: Route) => {
  // Set via a watcher as it wasn't reliably recalculating when using directly in a getter
  currentRoute.value = newValue;
}, { immediate: true });

onMounted(() => {
  appEventBus.on('openSearch', handleOpenSearch);
});

onUnmounted(() => {
  appEventBus.off('openSearch', handleOpenSearch);
});

function handleOpenSearch(event: {location: SearchLocation}): void {
  openSearchOverlay(event.location);
}

return { __sfc: true,SearchEntryComponent, UserStatusDialog, DoNotDisturbUntilCustomDialog, userStore, uiStateStore, route, allNotifications, overlayNames, props, userIdent, thisMenu, notificationIcon, searchTerm, searchOverlayLocation, headerBottom, privacyMode, currentRoute, showUserStatusDialog, displayCustomDoNotDisturbDialog, overlayOpenStates, isOverlayOpen, closeOverlays, openOverlays, openOverlay, closeAllOverlaysExcept, closeUserStatusDialog, userStatusClicked, currentUser, currentUserStatus, userDisplayName, isHomeRoute, isMobile, isMobileHeader, numNotifications, formattedNotificationCount, selectedPage, showHelpIcon, openSearchOverlay, currentLocation, extraMenuItems, shouldShowWorkspacePublishingItem, shouldShowKBItem, selectedOverflowMenuItem, routeNameMatchesCurrentRoute, overflowMenuItemSelected, addOverflowMenuItem, closeSearchOverlay, togglePrivacyMode, toggleAppearOfflineClicked, showCustomDoNotDisturbDialog, hideCustomDoNotDisturbDialog, displayNotificationCount, handleOpenSearch, PopupMenu, BrandIcon, NotificationDot, NotificationsPopup, UserIdent, ProfileContextMenu, UserStatusIcon }
}

})